<template>
  <div class="memory-form">
    <slot name="header" v-if="!submitted">
      <div class="memory-form__top">
        <div class="memory-form__header">
          <span
            >Send us your memories from your favorite St. Jude's Fitness Team
            event!</span
          >
          <v-btn class="close" @click="close" icon>
            <v-icon color="jude_yellow">$close</v-icon>
          </v-btn>
        </div>
        <hr class="hr-yellow" />
        <div class="memory-form__subhead">
          Use the space below to tell us about a 2020 event that you loved.
        </div>
      </div>
    </slot>
    <v-form
      lazy-validation
      v-model="isFormValid"
      ref="form"
      @submit.prevent="submit"
      v-if="!submitted"
    >
      <v-textarea
        counter="6000"
        maxlength="6000"
        v-model="form.description"
        label="Share your message"
        outlined
        :rules="[validation.required]"
        validate-on-blur
      ></v-textarea>
      <br />
      <Uploader
        placeholder-title="Click to Upload your own photo here"
        placeholder-desc="Files may not exceed 100mb"
        v-model="form.source"
      />

      <hr class="hr-yellow" />

      <div class="memory-form__your-info-header">
        <div class="memory-form__your-info">
          <span class="memory-form__section-label"> Your info </span>
        </div>
        <div class="memory-form__tip">* Required Information</div>
      </div>

      <v-layout :wrap="['xs', 'sm'].includes($size)">
        <v-flex xs12 sm12>
          <v-text-field
            label="First Name *"
            v-model="form.first_name"
            :rules="[validation.required]"
            validate-on-blur
          ></v-text-field>
        </v-flex>
        <v-flex class="px-3"></v-flex>
        <v-flex xs12 sm12>
          <v-text-field
            label="Last Name *"
            v-model="form.last_name"
            :rules="[validation.required]"
            validate-on-blur
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout class="memory-form__checkbox-wrapper">
        <v-checkbox v-model="agree" color="#ffffff">
          <template v-slot:label> </template>
        </v-checkbox>
        <div class="memory-form__checkbox-label">
          By submitting your story, you are agreeing to the possibility that
          your name, likeness, and words may be publicly posted on this site
          and/or any other official St. Jude Children’s Research Hospital social
          media or digital platforms. View our
          <a
            href="https://drive.google.com/file/d/1JGxF39z6RryyUnMjM3BiO15qU-pFHISJ/view"
            target="_blank"
            >photo release terms</a
          >.
        </div>
      </v-layout>
      <br />
      <v-btn
        :disabled="uploading"
        class="btn-submit"
        color="jude_lightred"
        type="submit"
        >{{ uploading ? `Uploading... ${progress}` : "Submit" }}</v-btn
      >

      <br />
      <br />
      <Recaptcha />
    </v-form>
    <div v-else class="pa-8 text-center">
      <v-btn class="close" @click="close" icon>
        <v-icon color="jude_yellow">$close</v-icon>
      </v-btn>
      <div style="margin: auto">
        <div class="h2">Thank You!</div>
        <p>Your memory has been shared for submission.</p>
      </div>
      <br />
      <label class="memory-form__copy-link">
        <div class="memory-form__copy-link-field">
          <input
            style="width: 300px"
            type="text"
            :value="location"
            ref="copy_input"
            readonly
          />
        </div>
        <v-btn
          class="mr-5 copy-link-btn"
          color="jude_lightred"
          @click="copyToClipboard"
        >
          Copy link
        </v-btn>
        <v-btn
          class="one-more-btn"
          color="jude_lightred"
          @click="submitted = false"
          >Share one more</v-btn
        >
      </label>
    </div>
  </div>
</template>

<script>
import formMixin from "@/mixins/form.mixin";
import { copyToClipboard } from "@/utils";

export default {
  name: "MemoryForm",
  extends: formMixin,
  data() {
    return {
      form: {},
      location: window.location.href,
      agree: true,
      progress: 0,
      uploading: false,
      submitted: false,
    };
  },
  created() {
    this.reset();
  },
  methods: {
    copyToClipboard() {
      this.$refs.copy_input.select();
      copyToClipboard(this.$refs.copy_input.value);
    },
    reset() {
      this.form = {
        first_name: this.currentUser?.first_name || "",
        last_name: this.currentUser?.last_name || "",
      };
    },
    async submit() {
      if (!this.validate()) {
        return this.$alert(
          "Form is invalid, please fill all required fields",
          "error"
        );
      }
      if (!this.agree) {
        return this.$alert(
          "You must agree to the processing of personal data",
          "error"
        );
      }
      try {
        const formData = new FormData();
        this.uploading = true;

        this.form.page_slug = "stories";
        for (const key in this.form) {
          formData.append(`memory[${key}]`, this.form[key]);
        }

        await this.$recaptchaLoaded();
        const recaptcha = await this.$recaptcha("login");
        formData.append("recaptcha", recaptcha);

        await this.$api.Memory.create(formData, {
          onUploadProgress: (progress) => {
            this.progress = progress;
          },
        });
        this.uploading = false;
        this.progress = 0;
        this.reset();
        this.submitted = true;
      } catch {
        this.uploading = false;
        this.progress = 0;
      }
    },
    close() {
      if (!this.uploading) {
        this.reset();
        this.$emit("close");
        this.submitted = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.memory-form {
  position: relative;
  background: linear-gradient(0deg, $jude-darkred, $jude-darkred),
    url(~@/assets/ripple.jpg);
  background-blend-mode: multiply, normal;
  mix-blend-mode: darken;
  background-position: center;
  background-size: cover;
  padding: 50px 120px;
  color: white;

  p {
    padding: 0 10%;
  }

  a {
    color: white;
  }

  &__header {
    display: flex;
    align-items: center;
    text-align: center;
    color: white;
  }
  .h2,
  &__header {
    font-weight: 900;
    font-size: 36px;
    line-height: 45px;
    color: white;
  }

  &__top {
    hr {
      max-width: 819px;
      margin: 28px auto;
    }
  }

  .v-form {
    hr {
      height: 2px;
      background: white;
      margin: 55px 0 26px;
    }
  }

  &__subhead {
    text-align: center;
    color: white;
    font-size: 18px;
    margin-bottom: 26px;
  }

  &::v-deep {
    .v-textarea {
      .v-text-field__details {
        order: -1;
        padding-right: 0;
        padding-left: 0;
        .v-counter.theme--light,
        .v-messages.theme--light {
          color: white;
        }
      }
      .v-input__slot {
        background: white;
      }
    }
    .v-text-field .v-label,
    .theme--light.v-input input {
      color: white;
    }
    .v-text-field:not(.v-textarea) {
      .error--text,
      .v-label {
        color: white !important;
        &.v-label-active {
          opacity: 0;
        }
      }
    }
    .v-text-field.v-textarea .v-label {
      color: rgba(0, 0, 0, 0.6);
      .error--text {
        color: rgba(0, 0, 0, 0.6);
      }
      &.v-label--active {
        color: rgba(0, 0, 0, 0) !important;
      }
    }
    .v-text-field > .v-input__control > .v-input__slot:after,
    .theme--light.v-text-field > .v-input__control > .v-input__slot:before {
      border-color: white;
    }
  }

  .btn-submit {
    color: white;
  }

  &__tags::v-deep .tags {
    justify-content: normal;
    margin: 0 -5px;
  }

  &__title {
    color: $primary;
    font-weight: bold;
    font-size: 18px;
    margin-right: 15px;
    display: block;
  }

  &__section-label {
    font-weight: 800;
    font-size: 24px;
    line-height: 30px;
    color: white;
  }

  &__tip {
    text-align: right;
    font-size: 12px;
  }

  &__your-info {
    margin: 30px 0 20px;
  }

  &__copy-link {
    .v-btn {
      margin: 20px 10px !important;
    }

    &-field {
      text-align: center;
      border: 1px solid #c4c4c4;
      border-radius: 45px;
      font-family: $body-font-family;
      font-size: 14px;
      color: white;
      display: flex;
      align-items: center;
      padding: 0 20px;
      margin: auto;
      width: 300px;
      height: 36px;

      input {
        outline: none !important;
        text-align: center;
        color: white;
      }
    }
  }
}
.v-btn--outlined .v-btn__content .v-icon,
.v-btn--round .v-btn__content .v-icon {
  &::v-deep {
    color: $primary;
  }
}

.copy-link-btn,
.one-more-btn {
  color: white;
}

@media screen and (max-width: 960px) {
  .memory-form {
    padding: 20px;
    form {
      padding: 0;
    }

    &__top {
      padding: 0 17px;
    }

    &__title {
      width: 100%;
    }

    &__header {
      padding: 20px;
    }

    &__your-info {
      margin-top: 10px;
      &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }

    &__checkbox-wrapper {
      margin: 40px 0;
      .v-input--selection-controls {
        margin-top: 0;
      }
    }

    &::v-deep {
      .v-btn:not(.v-btn--round).v-size--default.btn-submit {
        font-size: 16px;
        height: 36px;
        padding: 0 22px;
      }
    }
  }
}
</style>
